/**
 * Gregorian ↔ Hebrew conversion (Fourmilab / Calendrical Calculations, public domain).
 * Months: 1=Nisan … 6=Elul, 7=Tishrei … 12=Adar, 13=Adar II.
 */

export const HEBREW_MONTH_NAMES: Record<number, string> = {
  1: 'Nisan',
  2: 'Iyar',
  3: 'Sivan',
  4: 'Tammuz',
  5: 'Av',
  6: 'Elul',
  7: 'Tishrei',
  8: 'Cheshvan',
  9: 'Kislev',
  10: 'Tevet',
  11: 'Shevat',
  12: 'Adar',
  13: 'Adar II',
};

/** Tishrei → Elul, with Adar I/II folded into Adar so Purim and Pesach line up. */
export const TISHREI_AXIS_MONTHS = [
  'Tishrei',
  'Cheshvan',
  'Kislev',
  'Tevet',
  'Shevat',
  'Adar',
  'Nisan',
  'Iyar',
  'Sivan',
  'Tammuz',
  'Av',
  'Elul',
] as const;

export const HEBREW_YEAR_WEEKS = 52;
const HEBREW_EPOCH = 347995.5;

export interface HebrewDate {
  year: number;
  month: number;
  day: number;
  month_name: string;
  is_leap: boolean;
}

function mod(a: number, b: number): number {
  return a - b * Math.floor(a / b);
}

export function is_hebrew_leap_year(year: number): boolean {
  return mod(year * 7 + 1, 19) < 7;
}

function hebrew_year_months(year: number): number {
  return is_hebrew_leap_year(year) ? 13 : 12;
}

function hebrew_delay_1(year: number): number {
  const months = Math.floor((235 * year - 234) / 19);
  const parts = 12084 + 13753 * months;
  let day = months * 29 + Math.floor(parts / 25920);
  if (mod(3 * (day + 1), 7) < 3) {
    day += 1;
  }
  return day;
}

function hebrew_delay_2(year: number): number {
  const last = hebrew_delay_1(year - 1);
  const present = hebrew_delay_1(year);
  const next = hebrew_delay_1(year + 1);
  if (next - present === 356) {
    return 2;
  }
  if (present - last === 382) {
    return 1;
  }
  return 0;
}

function leap_gregorian(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function gregorian_to_jd(year: number, month: number, day: number): number {
  return (
    1721424.5 +
    365 * (year - 1) +
    Math.floor((year - 1) / 4) -
    Math.floor((year - 1) / 100) +
    Math.floor((year - 1) / 400) +
    Math.floor((367 * month - 362) / 12) +
    (month <= 2 ? 0 : leap_gregorian(year) ? -1 : -2) +
    day
  );
}

function jd_to_gregorian(jd: number): { y: number; mo: number; d: number } {
  const wjd = Math.floor(jd - 0.5) + 0.5;
  const depoch = wjd - 1721425.5;
  const quadricent = Math.floor(depoch / 146097);
  const dqc = mod(depoch, 146097);
  const cent = Math.floor(dqc / 36524);
  const dcent = mod(dqc, 36524);
  const quad = Math.floor(dcent / 1461);
  const dquad = mod(dcent, 1461);
  const yindex = Math.floor(dquad / 365);
  let year = quadricent * 400 + cent * 100 + quad * 4 + yindex;
  if (!(cent === 4 || yindex === 4)) {
    year += 1;
  }
  const yearday = wjd - gregorian_to_jd(year, 1, 1);
  const leapadj = wjd < gregorian_to_jd(year, 3, 1) ? 0 : leap_gregorian(year) ? 1 : 2;
  const month = Math.floor(((yearday + leapadj) * 12 + 373) / 367);
  const day = wjd - gregorian_to_jd(year, month, 1) + 1;
  return { y: year, mo: month, d: day };
}

export function hebrew_to_ymd(year: number, month: number, day: number): string {
  const { y, mo, d } = jd_to_gregorian(hebrew_to_jd(year, month, day));
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** First civil day of the Hebrew month that contains `ymd`. */
export function hebrew_month_start_ymd(ymd: string): string {
  const h = gregorian_to_hebrew(ymd);
  return hebrew_to_ymd(h.year, h.month, 1);
}

export function hebrew_month_days(year: number, month: number): number {
  if (month === 2 || month === 4 || month === 6 || month === 10 || month === 13) {
    return 29;
  }
  if (month === 12 && !is_hebrew_leap_year(year)) {
    return 29;
  }
  if (month === 8 && !(mod(hebrew_year_days(year), 10) === 5)) {
    return 29;
  }
  if (month === 9 && mod(hebrew_year_days(year), 10) === 3) {
    return 29;
  }
  return 30;
}

function hebrew_to_jd(year: number, month: number, day: number): number {
  const months = hebrew_year_months(year);
  let jd = HEBREW_EPOCH + hebrew_delay_1(year) + hebrew_delay_2(year) + day + 1;
  if (month < 7) {
    for (let mon = 7; mon <= months; mon++) {
      jd += hebrew_month_days(year, mon);
    }
    for (let mon = 1; mon < month; mon++) {
      jd += hebrew_month_days(year, mon);
    }
  } else {
    for (let mon = 7; mon < month; mon++) {
      jd += hebrew_month_days(year, mon);
    }
  }
  return jd;
}

function hebrew_year_days(year: number): number {
  return hebrew_to_jd(year + 1, 7, 1) - hebrew_to_jd(year, 7, 1);
}

function jd_to_hebrew(jd: number): HebrewDate {
  const noon = Math.floor(jd) + 0.5;
  let count = Math.floor(((noon - HEBREW_EPOCH) * 98496.0) / 35975351.0);
  let year = count - 1;
  for (let i = count; noon >= hebrew_to_jd(i, 7, 1); i++) {
    year = i;
  }
  const first = noon < hebrew_to_jd(year, 1, 1) ? 7 : 1;
  let month = first;
  for (let i = first; noon > hebrew_to_jd(year, i, hebrew_month_days(year, i)); i++) {
    month = i + 1;
  }
  const day = noon - hebrew_to_jd(year, month, 1) + 1;
  return {
    year,
    month,
    day,
    month_name: HEBREW_MONTH_NAMES[month] || `M${month}`,
    is_leap: is_hebrew_leap_year(year),
  };
}

function parse_ymd(ymd: string): { y: number; mo: number; d: number } {
  return {
    y: parseInt(ymd.slice(0, 4), 10),
    mo: parseInt(ymd.slice(5, 7), 10),
    d: parseInt(ymd.slice(8, 10), 10),
  };
}

export function gregorian_to_hebrew(ymd: string): HebrewDate {
  const { y, mo, d } = parse_ymd(ymd);
  return jd_to_hebrew(gregorian_to_jd(y, mo, d));
}

/** 0=Tishrei … 11=Elul. Adar I and Adar II share Adar so Purim stays aligned. */
export function tishrei_month_slot(month: number): number {
  if (month === 12 || month === 13) {
    return 5;
  }
  if (month >= 7) {
    return month - 7;
  }
  return month + 5;
}

export function hebrew_week_index(h: HebrewDate): number {
  const slot = tishrei_month_slot(h.month);
  const day = Math.min(30, Math.max(1, h.day));
  const approx = slot * 30 + day;
  return Math.max(0, Math.min(HEBREW_YEAR_WEEKS - 1, Math.floor((approx - 1) / 7)));
}

export function hebrew_week_index_for_ymd(ymd: string): number {
  return hebrew_week_index(gregorian_to_hebrew(ymd));
}

export function format_hebrew_date(h: HebrewDate): string {
  return `${h.day} ${h.month_name} ${h.year}`;
}

export function format_hebrew_date_ymd(ymd: string): string {
  return format_hebrew_date(gregorian_to_hebrew(ymd));
}

export function month_tick_week_index(slot: number): number {
  return Math.max(0, Math.min(HEBREW_YEAR_WEEKS - 1, Math.round((slot * 30 + 1) / 7)));
}

export function hebrew_year_for_ymd(ymd: string): number {
  return gregorian_to_hebrew(ymd).year;
}

/** Tishrei-axis label for a 0-based Hebrew week index (no Gregorian date). */
export function hebrew_axis_label(week_index: number): string {
  const approx_day = Math.max(0, week_index) * 7 + 1;
  const slot = Math.min(TISHREI_AXIS_MONTHS.length - 1, Math.floor((approx_day - 1) / 30));
  const day = ((approx_day - 1) % 30) + 1;
  return `${day} ${TISHREI_AXIS_MONTHS[slot]}`;
}
